import { EmptyState, MessageIcon } from "@rhema/ui";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useIsDesktop } from "../app/media";
import { useClient, useClientState } from "../session/session";
import { homeSpaceId, spaceHref } from "../spaces/data";

/**
 * Opens a link to a message, /m/<chat>/<message>. It finds where the message lives and goes there:
 * the chat at that message, or for a reply in a thread, the thread at that reply. Someone who is not
 * in the chat is told it is not available to them.
 */
export function MessageLink() {
  const { chatId = "", messageId = "" } = useParams();
  const client = useClient();
  const state = useClientState();
  const navigate = useNavigate();
  const desktop = useIsDesktop();
  const [problem, setProblem] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: resolves once per link
  useEffect(() => {
    let current = true;
    setProblem(null);
    client
      .messagePage(chatId, messageId)
      .then((page) => {
        if (!current) return;
        const chat = state.chats[chatId];
        if (page.chat_kind === "channel") {
          const spaceId = chat ? homeSpaceId(state, chat) : (page.space_id ?? "");
          const channel = spaceHref(spaceId, chatId);
          if (page.thread_root) {
            const reply = `reply=${encodeURIComponent(messageId)}`;
            navigate(
              desktop
                ? `${channel}?thread=${page.thread_root}&${reply}`
                : `${channel}/thread/${page.thread_root}?${reply}`,
              { replace: true },
            );
          } else navigate(`${channel}?m=${encodeURIComponent(messageId)}`, { replace: true });
          return;
        }
        navigate(`/chats/${chatId}?m=${encodeURIComponent(messageId)}`, { replace: true });
      })
      .catch((e: { status?: number; message?: string }) => {
        if (!current) return;
        setProblem(
          e.status === 404 || e.status === 403
            ? "It may have been deleted, or you are not in the chat it was sent in."
            : e.message || "The message could not be opened. Try again.",
        );
      });
    return () => {
      current = false;
    };
  }, [client, chatId, messageId]);

  return problem ? (
    <EmptyState
      icon={<MessageIcon size={26} />}
      title="This message is not available to you"
      className="flex-1"
    >
      {problem}
    </EmptyState>
  ) : (
    <EmptyState icon={<MessageIcon size={26} />} title="Opening the message" className="flex-1" />
  );
}
